import React from 'react';
import { Trophy, Crown, Users, Medal } from 'lucide-react';
import FeaturePage from './FeaturePage';

export default function Leagues() {
  return (
    <FeaturePage
      seo={{
        title: 'Football Leagues & Tournaments - Compete Online | My Football Game',
        description: 'Climb through 5 league tiers, create private leagues with friends, and enter weekly knockout tournaments in My Football Game. Free online football league system.',
        path: '/features/leagues',
      }}
      eyebrow="Feature Spotlight"
      title="LEAGUES & "
      titleAccent="TOURNAMENTS"
      subtitle="Five divisions with promotion and relegation, private leagues for friend groups, and weekly knockout tournaments with big prize pools."
      breadcrumbLabel="Leagues"
      heroContent={
        <div className="grid grid-cols-5 gap-3 mb-16 max-w-3xl mx-auto">
          {[
            { tier: 1, name: 'Amateur League', color: '#6b7280' },
            { tier: 2, name: 'Regional League', color: '#3b82f6' },
            { tier: 3, name: 'National League', color: '#8b5cf6' },
            { tier: 4, name: 'Championship', color: '#f59e0b' },
            { tier: 5, name: 'Elite League', color: '#ef4444' },
          ].map((l) => (
            <div key={l.tier} className="rounded-lg p-3 text-center" style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}>
              <Trophy className="w-5 h-5 mx-auto mb-1" style={{ color: l.color }} />
              <div className="text-xs font-bold">{l.name}</div>
              <div className="text-[10px] text-[var(--text-3)]">Tier {l.tier}</div>
            </div>
          ))}
        </div>
      }
      sections={[
        {
          icon: <Crown className="w-5 h-5" />,
          title: 'Promotion & Relegation',
          content: (
            <>
              <p>Every manager starts in the Amateur League. Each season is 18 matchdays in a 10-club division: the top two clubs are promoted and the bottom two are relegated.</p>
              <p>The five divisions are the Amateur League, Regional League, National League, Championship and Elite League. Higher divisions pay a bigger season prize, and the competition gets tougher with every promotion.</p>
            </>
          ),
        },
        {
          icon: <Users className="w-5 h-5" />,
          title: 'Private Leagues',
          content: (
            <>
              <p>Create a private league, share the invite code with your friends, and compete head-to-head in your own closed competition. Private leagues have their own standings, match history, and bragging rights.</p>
              <p>Private leagues support unlimited members and run alongside the main league system. Challenge friends within your league to schedule competitive matches that count towards your private league standings.</p>
            </>
          ),
        },
        {
          icon: <Trophy className="w-5 h-5" />,
          title: 'Weekly Tournaments',
          content: (
            <>
              <p>Enter weekly knockout tournaments for a chance to win big prize pools. Four tiers are available — Bronze, Silver, Gold, and Diamond — each with different entry fees and rewards.</p>
              <p>Tournaments follow a standard knockout format: quarter-finals, semi-finals, and a final. Win three matches to take home the top prize. A single loss eliminates you, making squad depth and tactics critical.</p>
            </>
          ),
        },
        {
          icon: <Medal className="w-5 h-5" />,
          title: 'Global Leaderboard',
          content: (
            <>
              <p>The <a href="/leaderboard" style={{ color: 'var(--accent)' }}>global leaderboard</a> ranks every manager by their overall score, which combines wins, draws, and goals. Separate leaderboards track top scorers, assist leaders, highest-rated players, and best defenses.</p>
              <p>Climbing the leaderboard proves you are one of the best managers in the game. The top positions are visible to every player — earn your place at the summit.</p>
            </>
          ),
        },
      ]}
      faqItems={[
        { q: 'How do I get promoted to a higher league?', a: 'Finish in the top two of your division at the end of the 18-matchday season. The bottom two clubs are relegated.' },
        { q: 'Can I create a private league with my friends?', a: 'Yes. Create a private league from the Leagues tab, share the invite code with your friends, and start challenging each other. Private leagues run alongside the main league system.' },
        { q: 'How do weekly tournaments work?', a: 'Tournaments are knockout competitions with 8 teams. Pay an entry fee in coins or gold, then win three matches (quarter-final, semi-final, final) to claim the top prize. New tournaments launch every Monday.' },
        { q: 'What rewards do I get for winning matches?', a: 'Every win earns coins, and higher divisions pay a bigger season prize. Tournament wins offer prize pools that can be much larger than regular match rewards.' },
      ]}
      ctaTitle="Start climbing the leagues"
      ctaText="Create your club for free and begin your journey from the Amateur League to the Elite League."
    />
  );
}
